import { useState } from "react";
import { Gift, Copy, Share2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useReferralCode } from "@/hooks/useReferrals";
import { toast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import ShareDialog from "@/components/ShareDialog";

export default function ReferralBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: referralCode } = useReferralCode();
  const code = referralCode?.code || "...";
  const siteUrl = "https://gatornotes.lovable.app";
  const referralLink = `${siteUrl}/auth?ref=${code}`;
  const [shareOpen, setShareOpen] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(code).then(() => {
      toast({ title: "Referral code copied!", description: code });
    }).catch(() => {
      toast({ title: "Referral code", description: code });
    });
  };

  if (!user) {
    return (
      <div className="w-full bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20">
        <div className="container mx-auto px-4 py-3 flex items-center justify-center gap-3 text-center">
          <Gift className="h-4 w-4 text-primary shrink-0" />
          <p className="text-sm text-foreground">
            <span className="font-semibold">Invite friends. Get rewarded.</span>
            <span className="text-muted-foreground ml-1 hidden sm:inline">
              Sign up and get a free week of Plus with a referral code.
            </span>
          </p>
          <Button variant="hero" size="sm" className="h-7 text-xs shrink-0" onClick={() => navigate("/auth?mode=signup")}>
            Sign up
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-b border-primary/20">
      <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3">
        <div className="flex items-center gap-2 text-center sm:text-left">
          <Gift className="h-4 w-4 text-primary shrink-0" />
          <div>
            <span className="text-sm font-semibold text-foreground">Invite friends. Get rewarded.</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground ml-1.5 hidden sm:inline cursor-help underline decoration-dotted">
                  Get 1 FREE week of Plus for you & your friend.
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[250px] text-xs">
                Your friend must sign up using your code for both of you to receive the reward.
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <code className="rounded-md bg-primary/10 border border-primary/20 px-2.5 py-1 text-xs font-mono font-bold text-primary">
            {code}
          </code>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={copyCode}>
            <Copy className="h-3 w-3" /> Copy
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setShareOpen(true)}>
            <Share2 className="h-3 w-3" /> Share
          </Button>
          <ShareDialog
            open={shareOpen}
            onOpenChange={setShareOpen}
            title="Invite Friends to GatorNotes"
            url={referralLink}
            message={`Join GatorNotes — the student marketplace to buy & sell course notes, powered by AI study tools. Sign up with my code ${code} and we both get a free week of Plus: ${referralLink}`}
          />
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 hidden sm:inline-flex" asChild>
            <Link to="/referrals">
              Dashboard <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
